import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Modal, Alert, Badge, Dropdown } from 'react-bootstrap';
import { Plus, Edit, Trash2, Search, ArrowRight, FileText, Mail, CheckCircle, XCircle, Clock } from 'lucide-react';
import { quoteAPI, clientAPI, productAPI, invoiceAPI } from '../services/api';

const API_BASE_URL = 'https://backfacturas.rmtagency.es/api';

interface Client {
  id: number;
  name: string;
  email: string;
  taxId: string;
}

interface Product {
  id: number;
  name: string;
  price: number;
  taxRate: number;
  unit: string;
}

interface QuoteItem {
  id?: number;
  product?: Product;
  productName: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  lineTotal: number;
}

interface Quote {
  id: number;
  client: Client;
  quoteNumber: string;
  quoteDate: string;
  expiryDate: string;
  status: string;
  subtotal: number;
  taxAmount: number;
  total: number;
  notes: string;
  items: QuoteItem[];
  createdAt: string;
  updatedAt: string;
}

const Presupuestos: React.FC = () => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    clientId: '',
    quoteNumber: '',
    quoteDate: '',
    expiryDate: '',
    notes: '',
    items: [] as QuoteItem[],
    status: 'BORRADOR'
  });

  useEffect(() => {
    fetchQuotes();
    fetchClients();
    fetchProducts();
  }, []);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const response = await quoteAPI.getAll();
      console.log('Raw response from API:', response.data);
      // Check for duplicates in the response
      response.data.forEach((quote: Quote, index: number) => {
        console.log(`Quote ${index} (ID: ${quote.id}) has ${quote.items?.length || 0} items`);
        if (quote.items?.length) {
          console.log(`Quote ${index} items:`, quote.items);
        }
      });
      setQuotes(response.data);
    } catch (err) {
      setError('Error al cargar los presupuestos');
      console.error('Error fetching quotes:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientAPI.getAll();
      setClients(response.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productAPI.getActive();
      setProducts(response.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const quoteData = {
        ...formData,
        clientId: parseInt(formData.clientId),
        items: formData.items.map(item => ({
          ...item,
          productId: item.product?.id
        }))
      };
      
      if (editingQuote) {
        await quoteAPI.update(editingQuote.id, quoteData);
        // Update status if it changed
        if (formData.status !== editingQuote.status) {
          await quoteAPI.updateStatus(editingQuote.id, formData.status);
        }
      } else {
        await quoteAPI.create(quoteData);
      }
      setShowModal(false);
      setEditingQuote(null);
      resetForm();
      fetchQuotes();
    } catch (err) {
      setError('Error al guardar el presupuesto');
      console.error('Error saving quote:', err);
    }
  };

  const handleEdit = (quote: Quote) => {
    console.log('handleEdit called with quote:', quote);
    console.log('Original items count:', quote.items?.length);
    console.log('Original items:', quote.items);
    
    setEditingQuote(quote);
    
    const mappedItems = quote.items ? quote.items.map(item => ({
      ...item,
      product: item.product || undefined
    })) : [];
    
    console.log('Mapped items count:', mappedItems.length);
    console.log('Mapped items:', mappedItems);
    
    setFormData({
      clientId: quote.client.id.toString(),
      quoteNumber: quote.quoteNumber || '',
      quoteDate: quote.quoteDate,
      expiryDate: quote.expiryDate || '',
      notes: quote.notes || '',
      status: quote.status || 'BORRADOR',
      items: mappedItems
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este presupuesto?')) {
      try {
        await quoteAPI.delete(id);
        fetchQuotes();
      } catch (err) {
        setError('Error al eliminar el presupuesto');
        console.error('Error deleting quote:', err);
      }
    }
  };

  const handleConvertToInvoice = async (quote: Quote) => {
    if (window.confirm('¿Convertir este presupuesto en factura?')) {
      try {
        // Use the dedicated conversion endpoint instead of creating manually
        await quoteAPI.convertToInvoice(quote.id, {
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // +30 days
        });
        alert('Presupuesto convertido a factura correctamente');
        fetchQuotes();
      } catch (err) {
        setError('Error al convertir el presupuesto a factura');
        console.error('Error converting quote to invoice:', err);
      }
    }
  };

  const handleUpdateStatus = async (quoteId: number, newStatus: string) => {
    try {
      await quoteAPI.updateStatus(quoteId, newStatus);
      fetchQuotes();
    } catch (err) {
      setError('Error al actualizar el estado del presupuesto');
      console.error('Error updating quote status:', err);
    }
  };

  const handleGeneratePDF = async (id: number) => {
    try {
      // Direct download approach without blob URLs
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = `${API_BASE_URL}/quotes/${id}/pdf`;
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `presupuesto-${id}.pdf`);
      
      // Add authorization header by creating a temporary form
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/pdf'
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        link.href = downloadUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        throw new Error('Error al descargar el PDF');
      }
    } catch (err) {
      setError('Error al generar el PDF');
      console.error('Error generating PDF:', err);
    }
  };

  const handleSendEmail = async (id: number) => {
    try {
      await quoteAPI.sendEmail(id);
      alert('Presupuesto enviado por email correctamente');
      // Update status to SENT
      await handleUpdateStatus(id, 'SENT');
    } catch (err) {
      setError('Error al enviar el presupuesto por email');
      console.error('Error sending quote email:', err);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        productName: '',
        description: '',
        quantity: 1,
        unitPrice: 0,
        taxRate: 21,
        lineTotal: 0
      }]
    });
  };

  const removeItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...formData.items];
    if (field === 'product') {
      const product = products.find(p => p.id === parseInt(value));
      if (product) {
        newItems[index] = {
          ...newItems[index],
          product,
          productName: product.name,
          unitPrice: product.price,
          taxRate: product.taxRate
        };
      }
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    
    // Calculate line total
    const item = newItems[index];
    item.lineTotal = item.quantity * item.unitPrice;
    
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.lineTotal, 0);
    const taxAmount = formData.items.reduce((sum, item) => 
      sum + (item.lineTotal * item.taxRate / 100), 0);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const resetForm = () => {
    setFormData({
      clientId: '',
      quoteNumber: '',
      quoteDate: '',
      expiryDate: '',
      notes: '',
      status: 'BORRADOR',
      items: []
    });
  };

  const handleNewQuote = () => {
    setEditingQuote(null);
    resetForm();
    setShowModal(true);
  };

  const getStatusBadge = (status: string) => {
    const statusColors: { [key: string]: string } = {
      'BORRADOR': 'secondary',
      'ENVIADO': 'info',
      'ACEPTADO': 'success',
      'RECHAZADO': 'danger',
      'EXPIRADO': 'warning'
    };
    return <Badge bg={statusColors[status] || 'secondary'}>{status}</Badge>;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const isExpired = (expiryDate: string) => {
    return new Date(expiryDate) < new Date();
  };

  const { subtotal, taxAmount, total } = calculateTotals();

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Gestión de Presupuestos</h4>
              <Button variant="primary" onClick={handleNewQuote}>
                <Plus size={20} className="me-2" />
                Nuevo Presupuesto
              </Button>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group className="position-relative">
                    <Form.Control
                      type="text"
                      placeholder="Buscar presupuestos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Search size={20} className="position-absolute top-50 end-0 translate-middle-y me-3" />
                  </Form.Group>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                </div>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Número</th>
                      <th>Cliente</th>
                      <th>Fecha</th>
                      <th>Vencimiento</th>
                      <th>Total</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map((quote) => (
                      <tr key={quote.id} className={isExpired(quote.expiryDate) && quote.status === 'SENT' ? 'table-warning' : ''}>
                        <td>{quote.quoteNumber}</td>
                        <td>{quote.client.name}</td>
                        <td>{new Date(quote.quoteDate).toLocaleDateString('es-ES')}</td>
                        <td>
                          {quote.expiryDate ? (
                            <span className={isExpired(quote.expiryDate) ? 'text-danger fw-bold' : ''}>
                              {new Date(quote.expiryDate).toLocaleDateString('es-ES')}
                            </span>
                          ) : '-'}
                        </td>
                        <td>{formatPrice(quote.total)}</td>
                        <td>{getStatusBadge(quote.status)}</td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEdit(quote)}
                              title="Editar"
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handleGeneratePDF(quote.id)}
                              title="Descargar PDF"
                            >
                              <FileText size={16} />
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleSendEmail(quote.id)}
                              title="Enviar por email"
                            >
                              <Mail size={16} />
                            </Button>
                            {(quote.status === 'ENVIADO' || quote.status === 'ACEPTADO') && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleConvertToInvoice(quote)}
                                title="Convertir a factura"
                              >
                                <ArrowRight size={16} />
                              </Button>
                            )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(quote.id)}
                              title="Eliminar"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal para crear/editar presupuesto */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingQuote ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Cliente *</Form.Label>
                  <Form.Select
                    required
                    value={formData.clientId}
                    onChange={(e) => setFormData({...formData, clientId: e.target.value})}
                  >
                    <option value="">Seleccionar cliente...</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name} - {client.taxId}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Número de Presupuesto</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Auto-generado"
                    value={formData.quoteNumber}
                    onChange={(e) => setFormData({...formData, quoteNumber: e.target.value})}
                  />
                  <Form.Text className="text-muted">
                    Si se deja vacío, se generará automáticamente
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha Presupuesto *</Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={formData.quoteDate}
                    onChange={(e) => setFormData({...formData, quoteDate: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha Vencimiento</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            {editingQuote && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Estado</Form.Label>
                    <Form.Select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                    >
                      <option value="BORRADOR">Borrador</option>
                      <option value="ENVIADO">Enviado</option>
                      <option value="ACEPTADO">Aceptado</option>
                      <option value="RECHAZADO">Rechazado</option>
                      <option value="EXPIRADO">Expirado</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <hr />
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Líneas de Presupuesto</h5>
              <Button variant="outline-primary" size="sm" onClick={addItem}>
                <Plus size={16} className="me-1" />
                Añadir Línea
              </Button>
            </div>

            {(() => {
              console.log('About to render items. FormData items count:', formData.items.length);
              console.log('FormData items:', formData.items);
              return formData.items.map((item: QuoteItem, index: number) => (
              <Row key={index} className="align-items-end mb-2 p-2 border rounded">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Producto</Form.Label>
                    <Form.Select
                      value={item.product?.id || ''}
                      onChange={(e) => updateItem(index, 'product', e.target.value)}
                    >
                      <option value="">Producto personalizado...</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name} - {formatPrice(product.price)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Nombre</Form.Label>
                    <Form.Control
                      type="text"
                      value={item.productName}
                      onChange={(e) => updateItem(index, 'productName', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group>
                    <Form.Label>Cant.</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Precio</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.unitPrice}
                      onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                    />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group>
                    <Form.Label>IVA %</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.taxRate}
                      onChange={(e) => updateItem(index, 'taxRate', parseFloat(e.target.value) || 0)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Total</Form.Label>
                    <Form.Control
                      type="text"
                      readOnly
                      value={formatPrice(item.lineTotal)}
                    />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeItem(index)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </Col>
              </Row>
            ));
            })()}

            <Row className="mt-3">
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Notas</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Card>
                  <Card.Body>
                    <div className="d-flex justify-content-between">
                      <span>Subtotal:</span>
                      <span>{formatPrice(subtotal)}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>IVA:</span>
                      <span>{formatPrice(taxAmount)}</span>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between fw-bold">
                      <span>Total:</span>
                      <span>{formatPrice(total)}</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              {editingQuote ? 'Actualizar' : 'Crear'} Presupuesto
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Presupuestos;